import React from 'react';
import useDragList from './useDragList';
import { ceilPacks, clamp } from './math';

const thCompact = {
  whiteSpace: 'normal',
  lineHeight: 1.15,
  fontSize: 12,
  paddingTop: 6,
  paddingBottom: 6,
};
const cell = { verticalAlign: 'top' };
const inputFull = { width: '100%' };
const subLabel = { display: 'block', fontSize: 12, color: '#64748b', marginTop: 4 };

export default function MaterialsTable({ materials, onChange, onDel }) {
  const change = (i, k, v) => {
    const next = materials.slice();
    if (k === 'name' || k === 'packName' || k === 'url' || k === 'article') next[i] = { ...next[i], [k]: v };
    else next[i] = { ...next[i], [k]: clamp(v) };
    onChange(next);
  };

  const reorder = (next) => onChange(next);
  const { onDragStart, onDragOver, onDrop, onDragEnd } = useDragList(materials, reorder);

  return (
    <table className="zebra rt" id="tblMaterials">
      <thead>
        <tr>
          <th style={{ width: 34, ...thCompact }}>№</th>
          <th style={{ width: 200, ...thCompact }}>Материал<br />ссылка / артикул</th>

          <th style={thCompact}>Норма<br />(ед/м²)</th>
          <th style={thCompact}>Ед.<br />упак.</th>
          <th style={thCompact}>Цена, ₽/уп</th>
          <th style={{ width: 150, ...thCompact }}>Объём<br />м²/п.м</th>
          <th style={{ width: 150, ...thCompact }}>Нужно<br />упак.</th>
          <th style={thCompact}>Запас %</th>
          <th style={{ width: 120, ...thCompact }}>Сумма, ₽</th>
          <th style={{ width: 44, ...thCompact }} className="center">✖</th>
        </tr>
      </thead>

      <tbody>
        {(materials || []).map((m, i) => {
          const baseVol = m._manualVol ? Number(m._manualVolVal || 0) : Number(m.baseVol || 0);
          const norm = (m._normManual != null) ? Number(m._normManual || 0) : Number(m.norm || 0);
          const needUnits = baseVol * norm;
          const needWithExtra = needUnits * (1 + Number(m.extraPct || 0) / 100);
          const packsAuto = ceilPacks(needWithExtra, m.packSize || 1);
          const packs = m._manualPacks ? (m._manualPacksVal || 0) : (m._packsAuto ?? packsAuto);
          const sum = Math.round((packs * (m.price || 0)) || 0);

          return (
            <tr key={i} draggable onDragStart={onDragStart(i)} onDragOver={onDragOver(i)} onDrop={onDrop(i)} onDragEnd={onDragEnd}>
              <td style={cell}><div className="center"><span className="drag-handle">⋮⋮</span> {i + 1}</div></td>

              {/* материал */}
              <td style={{ ...cell, wordBreak: 'break-word' }}>
                <input
                  style={inputFull}
                  value={m.name || ''}
                  onChange={(e) => change(i, 'name', e.target.value)}
                  placeholder="Название материала"
                />
                <label style={subLabel}>
                  Ссылка:
                  <input
                    type="text"
                    style={{ ...inputFull, marginTop: 4 }}
                    value={m.url || ''}
                    onChange={(e) => change(i, 'url', e.target.value)}
                    placeholder="http://..."
                  />
                </label>
                <label style={subLabel}>
                  Артикул:
                  <input
                    type="text"
                    style={{ ...inputFull, marginTop: 4 }}
                    value={m.article || ''}
                    onChange={(e) => change(i, 'article', e.target.value)}
                    placeholder="например, SKU-12345"
                  />
                </label>
                {m.note ? <div className="muted small" style={{ marginTop: 4 }}>{m.note}</div> : null}
              </td>

              {/* остальное без изменений */}
              <td style={cell}>
                <input style={inputFull}
                  value={(m._normManual != null ? m._normManual : (m.norm ?? ''))}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = materials.slice();
                    next[i] = { ...next[i], _normManual: (v === '' ? null : clamp(v)) };
                    onChange(next);
                  }}
                  placeholder="по формуле/число"
                />
              </td>

              <td style={cell}>
                <input style={inputFull} value={m.packName || ''} onChange={(e) => change(i, 'packName', e.target.value)} placeholder="мешок 25 кг" />
                <label style={subLabel}>Размер:
                  <input style={{ width: 90, marginLeft: 6 }} value={m.packSize || 1} onChange={(e) => change(i, 'packSize', e.target.value)} />
                </label>
              </td>

              <td style={cell}>
                <input type="number" step="1" style={inputFull} value={m.price || 0} onChange={(e) => change(i, 'price', e.target.value)} />
              </td>

              <td style={cell}>
                <label className="muted small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!m._manualVol} onChange={(e) => { const next = materials.slice(); next[i] = { ...next[i], _manualVol: e.target.checked }; onChange(next); }} />
                  ручн.
                </label>
                <input type="number" step="0.01" disabled={!m._manualVol} value={m._manualVolVal ?? ''}
                  onChange={(e) => { const next = materials.slice(); next[i] = { ...next[i], _manualVolVal: clamp(e.target.value) }; onChange(next); }}
                  style={{ ...inputFull, marginTop: 4 }}
                />
                <div className="right small">= {Number.isFinite(baseVol) ? Number(baseVol).toFixed(2) : 0}</div>
              </td>

              <td style={cell}>
                <label className="muted small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!m._manualPacks} onChange={(e) => { const next = materials.slice(); next[i] = { ...next[i], _manualPacks: e.target.checked }; onChange(next); }} />
                  ручн.
                </label>
                <input type="number" step="0.001" disabled={!m._manualPacks} value={m._manualPacksVal ?? ''}
                  onChange={(e) => { const next = materials.slice(); next[i] = { ...next[i], _manualPacksVal: clamp(e.target.value) }; onChange(next); }}
                  style={{ ...inputFull, marginTop: 4 }}
                />
                <div className="right small">= {Number.isFinite(packs) ? packs : 0}</div>
              </td>

              <td style={cell}>
                <input type="number" step="1" style={inputFull} value={m.extraPct ?? 0} onChange={(e) => change(i, 'extraPct', e.target.value)} />
              </td>

              <td className="right" style={cell}>{sum.toLocaleString('ru-RU')}</td>
              <td className="center" style={cell}><button className="iconbtn" onClick={() => onDel(i)}>✕</button></td>
            </tr>
          );
        })}
      </tbody>

      <tfoot>
        <tr>
          <td colSpan={9} className="right">Итого материалы:</td>
          <td id="matTotal" className="right">
            {Number((materials || []).reduce((a, m) => a + (m.sum || 0), 0)).toLocaleString('ru-RU')}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
